import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";

/**
 * AnalyticsTracker — sends a page-view event to /api/analytics/page-view on
 * every route change. Captures UTM tags from the URL and a session ID
 * (stored in sessionStorage so reloads stay anonymous). GDPR-safe: no
 * cookies, IP gets hashed server-side.
 *
 * Mount once inside the <BrowserRouter> tree.
 */
const SESSION_KEY = "apex_session_id";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function readUtm(search) {
  const params = new URLSearchParams(search || "");
  const utm = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) utm[k.replace("utm_", "")] = v;
  });
  // Persist last-seen UTM in sessionStorage so subsequent SPA navigations
  // still attribute the origin until the tab closes.
  try {
    if (Object.keys(utm).length) {
      sessionStorage.setItem("apex_last_utm", JSON.stringify(utm));
    }
  } catch (_e) { /* sessionStorage may be unavailable (private mode) */ }
  if (!Object.keys(utm).length) {
    try {
      const stored = sessionStorage.getItem("apex_last_utm");
      if (stored) return JSON.parse(stored);
    } catch (_e) { /* ignore parse errors */ }
  }
  return utm;
}

export default function AnalyticsTracker() {
  const location = useLocation();
  const lastPath = useRef("");

  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPath.current === path) return;
    lastPath.current = path;

    const payload = {
      path: location.pathname,
      referer: document.referrer || "",
      sessionId: getSessionId(),
      utm: readUtm(location.search),
    };

    // Fire-and-forget. Auth header gets attached by axios interceptor.
    api.post("/analytics/page-view", payload).catch(() => {});
  }, [location.pathname, location.search]);

  return null;
}

/**
 * Helper used by AuthModal to attach origin UTM at registration time.
 */
export function getCurrentUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) utm[k.replace("utm_", "")] = v;
    });
    if (Object.keys(utm).length) return utm;
    const stored = sessionStorage.getItem("apex_last_utm");
    if (stored) return JSON.parse(stored);
  } catch (_e) { /* ignore */ }
  return null;
}
